import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
}

interface BreadcrumbsProps {
  breadcrumbItems: BreadcrumbItem[];
  showHome?: boolean;
  separator?: "chevron" | "slash" | "dot";
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  breadcrumbItems,
  showHome = true,
  separator = "chevron",
}) => {
  const navigate = useNavigate();

  const handleBreadcrumbClick = (path: string) => {
    if (path) {
      navigate(path);
    }
  };

  const items = showHome ? [...breadcrumbItems] : breadcrumbItems;

  const renderSeparator = () => {
    switch (separator) {
      case "slash":
        return <span className="text-gray-400 select-none">/</span>;
      case "dot":
        return <span className="text-gray-400 select-none">•</span>;
      default:
        return <ChevronRight className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <nav className="flex items-center" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-1 md:space-x-2">
        {items.map((item, index) => {
          const isLastItem = index === items.length - 1;

          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <span className="md:mx-1">{renderSeparator()}</span>
              )}

              {isLastItem ? (
                <span
                  className="flex items-center gap-1.5 px-2 py-1 text-sm md:text-base font-semibold text-blue-700 bg-blue-50 rounded-md"
                  aria-current="page"
                >
                  {item.icon}
                  <span className="truncate max-w-[150px] md:max-w-none">
                    {item.label}
                  </span>
                </span>
              ) : item.path ? (
                <button
                  onClick={() => handleBreadcrumbClick(item.path!)}
                  className="flex items-center gap-1.5 px-2 py-1 text-sm md:text-base text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-md transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                  aria-label={`Navigate to ${item.label}`}
                >
                  {item.icon}
                  <span className="truncate max-w-[120px] md:max-w-none">
                    {item.label}
                  </span>
                </button>
              ) : (
                <span className="flex items-center gap-1.5 px-2 py-1 text-sm md:text-base text-gray-500 cursor-default">
                  {item.icon}
                  <span className="truncate max-w-[120px] md:max-w-none">
                    {item.label}
                  </span>
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
